import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Alert, CircularProgress, Button, Chip, Stack } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

/**
 * StripePaymentForm - Credit Card Tokenization Component (Stripe version)
 * 
 * This component uses Stripe Elements to securely collect credit card info
 * and create a PaymentMethod (token) without your server ever touching raw card data.
 * 
 * HOW IT WORKS:
 * 1. Stripe Elements creates a secure iframe for card input
 * 2. User enters card details (card never touches your server)
 * 3. Stripe converts card to a PaymentMethod (e.g., "pm_abc123")
 * 4. PaymentMethod ID is passed to parent component via onTokenGenerated callback
 * 5. Parent component saves token with the ride data
 * 
 * IMPORTANT: You MUST set your Stripe Publishable Key in environment config!
 */

// Card element styling
const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        invalid: {
            color: '#9e2146',
            iconColor: '#9e2146',
        },
    },
    hidePostalCode: true,
};

// Inner component that uses Stripe hooks (must be inside <Elements>)
const StripeCardForm = forwardRef(({ onTokenGenerated, onTokenCleared, onError }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [savedToken, setSavedToken] = useState(null);
    const [cardReady, setCardReady] = useState(false);

    // Expose reset function to parent via ref
    useImperativeHandle(ref, () => ({
        reset: () => {
            setSavedToken(null);
            setError(null);
        },
        clearCard: () => {
            setSavedToken(null);
            if (onTokenCleared) {
                onTokenCleared();
            }
        }
    }));

    const handleCardChange = (event) => {
        setCardReady(event.complete);
        if (event.error) {
            setError(event.error.message);
        } else {
            setError(null);
        }
    };

    const tokenizeCard = async () => {
        if (!stripe || !elements) {
            const error = 'Payment form not initialized';
            setError(error);
            if (onError) onError(error);
            return null;
        }

        try {
            setError(null);
            setIsSaving(true);

            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

            if (stripeError) {
                console.error('❌ Tokenization failed:', stripeError.message);
                setError(stripeError.message);
                if (onError) onError(stripeError.message);
                return null;
            }

            // Save the token for display
            setSavedToken(paymentMethod.id);

            // Pass token to parent component
            if (onTokenGenerated) {
                onTokenGenerated(paymentMethod.id);
            }

            return paymentMethod.id;
        } catch (err) {
            const errorMessage = err.message || 'An error occurred processing the card';
            console.error('❌ Tokenization error:', err);
            setError(errorMessage);
            if (onError) onError(errorMessage);
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveCard = async () => {
        await tokenizeCard();
    };

    // Make tokenize function available globally (for parent to call)
    useEffect(() => {
        if (stripe && elements) {
            window.stripeTokenizeCard = tokenizeCard;
        }
        return () => {
            if (window.stripeTokenizeCard) {
                delete window.stripeTokenizeCard;
            }
        };
    }, [stripe, elements]);

    const handleRemoveCard = () => {
        setSavedToken(null);
        if (onTokenCleared) {
            onTokenCleared();
        }
    };

    // If card is already saved, show success state with remove option
    if (savedToken) {
        return (
            <Box sx={{ my: 2 }}>
                <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 2 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <span>Card saved successfully</span>
                        <Chip label="Prepaid" size="small" color="success" />
                    </Stack>
                </Alert>
                <Box sx={{
                    p: 2,
                    bgcolor: 'success.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon color="success" />
                        <span>Card on file will be charged when the ride is completed</span>
                    </Box>
                    <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveCard}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Remove Card
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ my: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Stripe Card Element Container */}
            <Box
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    padding: 2,
                    minHeight: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    '& .StripeElement': {
                        width: '100%'
                    }
                }}
            >
                <CardElement
                    options={CARD_ELEMENT_OPTIONS}
                    onChange={handleCardChange}
                />
            </Box>

            {/* Save Card Button */}
            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSaveCard}
                disabled={!stripe || !cardReady || isSaving}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
                sx={{ mt: 2 }}
            >
                {isSaving ? 'Saving Card...' : 'Save Card'}
            </Button>
        </Box>
    );
});

// Outer wrapper that provides the Stripe Elements context
// publishableKey is passed from PaymentForm (which fetches it from the server)
const StripePaymentForm = forwardRef(({ publishableKey, onTokenGenerated, onTokenCleared, onError }, ref) => {
    const [stripePromise, setStripePromise] = useState(null);
    const [loadError, setLoadError] = useState(null);

    // Use prop from server first, fall back to env var for backward compatibility
    const key = publishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    useEffect(() => {
        if (!key || key === 'your-stripe-publishable-key') {
            setLoadError('Stripe Publishable Key not configured! Ensure the server has StripeSettings:PublishableKey set.');
            return;
        }

        try {
            setStripePromise(loadStripe(key));
        } catch (err) {
            setLoadError('Failed to load Stripe. Please refresh the page.');
        }
    }, [key]);

    if (loadError) {
        return (
            <Box sx={{ my: 2 }}>
                <Alert severity="error">{loadError}</Alert>
            </Box>
        );
    }

    if (!stripePromise) {
        return (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Elements stripe={stripePromise}>
            <StripeCardForm
                ref={ref}
                onTokenGenerated={onTokenGenerated}
                onTokenCleared={onTokenCleared}
                onError={onError}
            />
        </Elements>
    );
});

export default StripePaymentForm;
